import { BrokeragePipeline, IBrokeragePipeline } from '@app/models/BrokeragePipeline'
import { ListingType } from '@app/models/enums'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum PipelineScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IPipelineListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IBrokeragePipeline>
  readonly listingType: ListingType
  readonly sellingAgent: string
  readonly scope: PipelineScope
  readonly sortOrder: string
  readonly orderBy: string
  readonly status: string
  readonly totalItems: number
  readonly totalPotentialProfit: number
  readonly year?: number
}

const PipelineListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<BrokeragePipeline>(),
  listingType: '',
  scope: PipelineScope.Mine,
  sellingAgent: 'All',
  sortOrder: 'ClosingDate',
  orderBy: 'Ascending',
  status: 'Active',
  totalItems: 0,
  totalPotentialProfit: 0,
  year: undefined,
})

export class PipelineListState extends PipelineListStateRecord implements IPipelineListState {
  public static fromJS(initialState?: Partial<IPipelineListState>) {
    if (!initialState) {
      return new PipelineListState()
    }

    const { items, ...rest } = initialState
    return new PipelineListState({
      items: items ? List(items.map((i) => BrokeragePipeline.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<BrokeragePipeline>
  public readonly listingType: ListingType
  public readonly sellingAgent: string
  public readonly scope: PipelineScope
  public readonly sortOrder: string
  public readonly orderBy: string
  public readonly status: string
  public readonly totalItems: number
  public readonly totalPotentialProfit: number
  public readonly year?: number

  constructor(props?: Partial<PipelineListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum PipelineListActionTypes {
  LoadPipeline = 'PIPELINE_LIST::LOAD_PIPELINE',
  LoadPipelineFailure = 'PIPELINE_LIST::LOAD_PIPELINE_FAILURE',
  LoadPipelineRequest = 'PIPELINE_LIST::LOAD_PIPELINE_REQUEST',
  LoadPipelineSuccess = 'PIPELINE_LIST::LOAD_PIPELINE_SUCCESS',
  SetFilterText = 'PIPELINE_LIST::SET_FILTER_TEXT',
  SetListingType = 'PIPELINE_LIST::SET_LISTING_TYPE',
  SetSellingAgent = 'PIPELINE_LIST::SET_PRIMARY_AGENT',
  SetScope = 'PIPELINE_LIST::SET_SCOPE',
  SetStatus = 'PIPELINE_LIST::SET_STATUS',
  SetSortOrder = 'PIPELINE_LIST::SET_SORT_ORDER',
  SetOrderBy = 'PIPELINE_LIST::SET_ORDER_BY',
  SetYear = 'PIPELINE_LIST::SET_YEAR',
}

interface ILoadPipelineAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: PipelineListActionTypes.LoadPipeline
}

interface ILoadPipelineFailureAction {
  error: string
  type: PipelineListActionTypes.LoadPipelineFailure
}

interface ILoadPipelineRequestAction {
  type: PipelineListActionTypes.LoadPipelineRequest
}

interface ILoadPipelineSuccessAction {
  append: boolean
  items: BrokeragePipeline[]
  totalItems: number
  totalPotentialProfit: number
  type: PipelineListActionTypes.LoadPipelineSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: PipelineListActionTypes.SetFilterText
}

interface ISetListingTypeAction {
  listingType: string
  type: PipelineListActionTypes.SetListingType
}

interface ISetSellingAgentAction {
  sellingAgent: string
  type: PipelineListActionTypes.SetSellingAgent
}

interface ISetScopeAction {
  scope: PipelineScope
  type: PipelineListActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: PipelineListActionTypes.SetSortOrder
}

interface ISetOrderByAction {
  orderBy: string
  type: PipelineListActionTypes.SetOrderBy
}

interface ISetStatusAction {
  status: string
  type: PipelineListActionTypes.SetStatus
}

interface ISetYearAction {
  year?: number
  type: PipelineListActionTypes.SetYear
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadPipelineFailureAction
  | ILoadPipelineRequestAction
  | ILoadPipelineSuccessAction
  | ISetFilterTextAction
  | ISetListingTypeAction
  | ISetSellingAgentAction
  | ISetScopeAction
  | ISetSortOrderAction
  | ISetOrderByAction
  | ISetStatusAction
  | ISetYearAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadPipeline(startIndex: number, stopIndex: number, append: boolean, loadComplete?: () => void): ILoadPipelineAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: PipelineListActionTypes.LoadPipeline,
  }
}

function loadPipelineFailure(error: string): ILoadPipelineFailureAction {
  return {
    error,
    type: PipelineListActionTypes.LoadPipelineFailure,
  }
}

function loadPipelineRequest(): ILoadPipelineRequestAction {
  return {
    type: PipelineListActionTypes.LoadPipelineRequest,
  }
}

function loadPipelineSuccess(items: BrokeragePipeline[], append: boolean, totalItems: number, totalPotentialProfit: number): ILoadPipelineSuccessAction {
  return {
    append,
    items,
    totalItems,
    totalPotentialProfit,
    type: PipelineListActionTypes.LoadPipelineSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: PipelineListActionTypes.SetFilterText,
  }
}

function setListingType(listingType: string): ISetListingTypeAction {
  return {
    listingType,
    type: PipelineListActionTypes.SetListingType,
  }
}

function setSellingAgent(sellingAgent: string): ISetSellingAgentAction {
  return {
    sellingAgent,
    type: PipelineListActionTypes.SetSellingAgent,
  }
}

function setScope(scope: PipelineScope): ISetScopeAction {
  return {
    scope,
    type: PipelineListActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: PipelineListActionTypes.SetSortOrder,
  }
}

function setOrderBy(orderBy: string) {
  return {
    orderBy,
    type: PipelineListActionTypes.SetOrderBy,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: PipelineListActionTypes.SetStatus,
  }
}

function setYear(year?: number): ISetYearAction {
  return {
    year,
    type: PipelineListActionTypes.SetYear,
  }
}

export const actionCreators = {
  loadPipeline,
  setFilterText,
  setListingType,
  setScope,
  setSellingAgent,
  setSortOrder,
  setOrderBy,
  setStatus,
  setYear,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PipelineListState> = (state: PipelineListState = new PipelineListState(), incomingAction: Action) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case PipelineListActionTypes.LoadPipelineFailure:
      return state.set('error', action.error).set('isLoading', false) as PipelineListState

    case PipelineListActionTypes.LoadPipelineRequest:
      return state.set('isLoading', true) as PipelineListState

    case PipelineListActionTypes.LoadPipelineSuccess:
      const newItems = action.append ? (state.items.concat(List(action.items)) as List<BrokeragePipeline>) : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalPotentialProfit', action.totalPotentialProfit)
        .set('totalItems', action.totalItems) as PipelineListState

    case PipelineListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as PipelineListState

    case PipelineListActionTypes.SetListingType:
      return state.set('listingType', action.listingType) as PipelineListState

    case PipelineListActionTypes.SetSellingAgent:
      return state.set('sellingAgent', action.sellingAgent) as PipelineListState

    case PipelineListActionTypes.SetScope:
      return state.set('scope', action.scope) as PipelineListState

    case PipelineListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as PipelineListState

    case PipelineListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as PipelineListState

    case PipelineListActionTypes.SetStatus:
      return state.set('status', action.status) as PipelineListState

    case PipelineListActionTypes.SetYear:
      return state.set('year', action.year) as PipelineListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadPipelineSaga(action: ILoadPipelineAction) {
  try {
    yield put(loadPipelineRequest())

    const currentState: PipelineListState = yield select((state: ApplicationState) => state.pipelineList)

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.sortOrder !== 'LastName') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (currentState.listingType) {
      query.push(`listingType=${currentState.listingType}`)
    }

    if (currentState.sellingAgent) {
      query.push(`agent=${currentState.sellingAgent}`)
    }

    if (currentState.year) {
      query.push(`year=${currentState.year}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = currentState.scope === PipelineScope.Mine ? '/api/BrokeragePipelines/My' : '/api/BrokeragePipelines'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: BrokeragePipeline[] = result.results.map((c: any) => BrokeragePipeline.fromJS(c))
      yield put(loadPipelineSuccess(contacts, action.append, result.paging.total, result.totalPotentialProfit))
    } else {
      yield put(loadPipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadPipeline() {
  yield takeLatest(PipelineListActionTypes.LoadPipeline, loadPipelineSaga)
}

export function* pipelineListRootSaga() {
  yield fork(watchLoadPipeline)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): PipelineListState =>
    state.pipelineList

export const selectError = () => createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () => createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () => createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () => createSelector(selectModuleState(), (mState) => mState.items)

export const selectListingtype = () => createSelector(selectModuleState(), (mState) => mState.listingType)

export const selectSellingAgent = () => createSelector(selectModuleState(), (mState) => mState.sellingAgent)

export const selectScope = () => createSelector(selectModuleState(), (mState) => mState.scope)

export const selectSortOrder = () => createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectOrderBy = () => createSelector(selectModuleState(), (mState) => mState.orderBy)

export const selectStatus = () => createSelector(selectModuleState(), (mState) => mState.status)

export const selectTotalItems = () => createSelector(selectModuleState(), (mState) => mState.totalItems)

export const selectTotalPotentialProfit = () => createSelector(selectModuleState(), (mState) => mState.totalPotentialProfit)

export const selectYear = () => createSelector(selectModuleState(), (mState) => mState.year)
